import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Box, DataTable, DataTableContainer, InfiniteScroll, Svg, Text } from '@hopskipdrive/react-components';
import { colorTokens, variables } from '@rideiq/shared/components/variables';
import { useQueryClient } from '@tanstack/react-query';

import { getBatchUploadLog } from '../../../api/services/BatchUploadFiles';
import { useInfiniteTableScroll } from '../../../hooks/useInfiniteTableScroll';
import { useTimezone } from '../../../hooks/useTimezone';
import { getDownloadFile } from '../../../utils/helpers/getDownloadFile';

import { SHOULD_POLL_UPLOAD_LOG_KEY } from './queryKeys';
import { ContentWrapper } from './styles';
import { useUploadCompletionPolling } from './useUploadCompletionPolling';

interface Uploads {
  id: number;
  totalRecords: number;
  createdCount: number;
  failedCount: number;
  updatedCount: number;
  failedRecordsFilename?: string;
  uploadedFilename: string;
  completedAt?: Date | string | null;
  createdAt: Date | string;
  updatedAt?: Date | string | null;
  fileType: 'rider';
  uploadedBy: string;
}

export const UploadsTable = (): ReactElement => {
  const [uploads, setUploads] = useState<Uploads[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { formatInTimezone } = useTimezone();
  const { pageParams, handlePagination } = useInfiniteTableScroll();
  const abortControllerRef = useRef(new AbortController());
  const [isFetchingFileToDownload, setIsFetchingFileToDownload] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const abortController = abortControllerRef.current;

    return () => {
      abortController.abort();
    };
  }, []);

  // A stale `true` from a previous visit should never keep polling going after the
  // user navigates away and back — only an upload that completes while this table is
  // currently mounted should re-arm it.
  useEffect(() => {
    queryClient.setQueryData(SHOULD_POLL_UPLOAD_LOG_KEY, false);
  }, [queryClient]);

  const getUploadsLog = useCallback(async (queryParams: QueryParams) => {
    setIsLoading(true);

    const response = await getBatchUploadLog(
      {
        perPage: 20,
        page: queryParams.page,
        batchUploadFileType: 'rider',
      },
      abortControllerRef.current.signal
    );

    if (abortControllerRef.current.signal.aborted) {
      return;
    }

    if (response?.type === 'success') {
      setUploads((prevUploads) => (queryParams.page === 1 ? response.data : prevUploads.concat(response.data)));
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    getUploadsLog({ ...pageParams });
  }, [pageParams]);

  const handlePage1Updated = useCallback((updatedPage1Uploads: Uploads[]) => {
    const updatedIds = new Set(updatedPage1Uploads.map((upload) => upload.id));

    setUploads((prevUploads) => [
      ...updatedPage1Uploads,
      ...prevUploads.filter((upload) => !updatedIds.has(upload.id)),
    ]);
  }, []);

  useUploadCompletionPolling(handlePage1Updated);

  const handleDownloadFile = async (fileToDownload, fileId, fileName) => {
    setIsFetchingFileToDownload(true);
    await getDownloadFile(fileToDownload, 'rider', fileId, fileName);
    setIsFetchingFileToDownload(false);
  };

  return (
    <ContentWrapper data-testid="rider-batch-uploads-logs-table-content">
      <DataTableContainer heightOffset={variables.headingHeights.withTitleTabs} minWidth="1292px">
        <InfiniteScroll
          itemCount={uploads.length}
          pageNumber={pageParams.page}
          perPage={20}
          handleNextPage={handlePagination}
        >
          <DataTable
            isLoading={isLoading}
            handleRowClick={() => null}
            columns={[
              {
                key: 'updatedAt',
                label: 'Updated on',
                align: 'left',
                valueSetter: (cellValue) => cellValue && formatInTimezone(cellValue, 'MM/dd/yyyy hh:mm aa'),
              },
              {
                key: 'uploadedBy',
                label: 'Uploaded by',
                align: 'left',
                valueSetter: (cellValue) => <Text noWrap>{cellValue}</Text>,
              },
              {
                key: 'updatedCount',
                label: 'Riders updated',
                align: 'left',
                valueSetter: (cellValue) => <Text noWrap>{cellValue}</Text>,
              },
              {
                key: 'createdCount',
                label: 'Riders created',
                align: 'left',
                valueSetter: (cellValue) => <Text noWrap>{cellValue}</Text>,
              },
              {
                label: 'Failed records',
                align: 'left',
                valueGetter: (rowData) => (
                  <Box
                    display="flex"
                    data-testid="rider-failed-records-file-download"
                    onClick={() =>
                      !isFetchingFileToDownload &&
                      rowData.failedCount > 0 &&
                      handleDownloadFile('failedRecordsFile', rowData.id, rowData.failedRecordsFilename)
                    }
                  >
                    <Text noWrap mr={2}>
                      {rowData.failedCount}
                    </Text>
                    {rowData.failedCount > 0 && <Svg color={colorTokens.action.active} icon="download" width="16px" />}
                  </Box>
                ),
              },
              {
                label: 'Total uploaded',
                align: 'left',
                valueGetter: (rowData) => (
                  <Box
                    display="flex"
                    data-testid="rider-original-file-download"
                    onClick={() =>
                      !isFetchingFileToDownload &&
                      rowData.totalRecords > 0 &&
                      handleDownloadFile('originalFile', rowData.id, rowData.uploadedFilename)
                    }
                  >
                    <Text noWrap mr={2}>
                      {rowData.totalRecords}
                    </Text>
                    {rowData.totalRecords > 0 && <Svg color={colorTokens.action.active} icon="download" width="16px" />}
                  </Box>
                ),
              },
            ]}
            rows={uploads}
          />
        </InfiniteScroll>
      </DataTableContainer>
    </ContentWrapper>
  );
};
